import {
  Box, Button, Grid, Stack,
} from '@mui/material';
import {
  useCallback, useContext, useMemo,
} from 'react';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import EditableText from '../../Form/EditableText';
import Utils from '../../../utils/Utils';
import '../../../assets/css/menu-list.css';
import BlockMenuListData from './BlockMenuListData';
import SubBlockMenuData from './SubBlockMenuData';
import BlockProps from '../../../models/BlockProps';
import ImageComponent from '../../ImageComponent';
import CloseButton from '../../Buttons/Close';
import BlockData from '../../../models/BlockData';
import { ImagesContext } from '../../../contexts/ImagesContext';
import LinkButton from '../../Buttons/LinkButton';
import ImageModal from '../../Modals/ImageModal/ImageModal';
import config from '../../../config';

export interface BlockMenuListProps extends BlockProps {
  blockData: BlockData[]
}

function BlockMenuList({
  data, isEditing, isTemplate, blockData, editHandler,
}: BlockMenuListProps) {
  const { placeHolderImage, images } = useContext(ImagesContext);
  const props = (data.data as BlockMenuListData);

  const handleTitleUpdate = (text?: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleAddSubBlock = () => {
    props.menus.push(SubBlockMenuData.getDefault(undefined, config.defaultImageUrl));
    editHandler(data.id, props);
  };

  const handleRemoveSubBlock = useCallback((index: number) => {
    props.menus.splice(index, 1);
    editHandler(data.id, props);
  }, [props, editHandler, data.id]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return props.menus?.map((menu, index) => (
        <Grid
          key={menu.id}
          item
          xs={4}
          minWidth={300}
          sx={[Utils.hoverBorder(isEditing), {
            position: 'relative',
            padding: 3,
          }]}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => handleRemoveSubBlock(index)}
          />
          {props.menus[index].image.value !== undefined && (
            <Box sx={[Utils.hoverBorder(isEditing), { display: 'flex', justifyContent: 'center', alignItems: 'center' }]}>
              <label htmlFor={`${data.id}_menu${index}`}>
                <ImageModal
                  disabled={isTemplate || !isEditing}
                  images={images}
                  handleOnConfirm={(base64image: string) => {
                    props.menus[index].image.value = base64image;
                    editHandler(data.id, props);
                  }}
                >
                  <ImageComponent src={menu.image.value} />
                </ImageModal>
              </label>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].image.value = undefined;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >

            {props.menus[index].title.value && (
              <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.menus[index].title.value = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <EditableText
                  text={menu.title.value}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={(text) => {
                    props.menus[index].title.value = text;
                    editHandler(data.id, props);
                  }}
                />
              </Box>
            )}

            {props.menus[index].price.value && (
              <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
                <CloseButton
                  show={isEditing}
                  updateHandler={() => {
                    props.menus[index].price.value = undefined;
                    editHandler(data.id, props);
                  }}
                />
                <EditableText
                  text={menu.price.value}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={(text) => {
                    props.menus[index].price.value = text;
                    editHandler(data.id, props);
                  }}
                />
              </Box>
            )}

          </Grid>

          {props.menus[index].description.value && (
            <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].description.value = undefined;
                  editHandler(data.id, props);
                }}
              />
              <EditableText
                text={menu.description.value}
                isEditable={isEditing && !isTemplate}
                updateHandler={(text) => {
                  props.menus[index].description.value = text;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

          {props.menus[index].button && (
            <Box sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.menus[index].button = undefined;
                  editHandler(data.id, props);
                }}
              />
              <LinkButton
                button={menu.button}
                blockData={blockData}
                isEditable={isEditing && !isTemplate}
                isTemplate={isTemplate}
                updateHandler={(item) => {
                  props.menus[index].button = item;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}

        </Grid>
      ));
    }

    return [];
  }, [props, isEditing, data.id, isTemplate, images, blockData, handleRemoveSubBlock, editHandler]);

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      sx={{
        minHeight: '250px',
        overflow: 'hidden',
        py: 1.5,
        backgroundImage: `url(${props.backgroundImage?.value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundImage: `url(${props.titleImage?.value || placeHolderImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >

        {(isEditing && !isTemplate) && (
          <ImageModal
            title="背景設定"
            images={images}
            handleOnConfirm={(base64image: string) => {
              props.titleImage.value = base64image;
              editHandler(data.id, props);
            }}
          />
        )}

        <Grid item alignItems="center" xs={12} sx={{ minHeight: '46px' }}>
          {props.title.value && (
            <Box sx={Utils.hoverBorder(isEditing)}>
              <EditableText
                text={props.title.value}
                isEditable={isEditing && !isTemplate}
                updateHandler={handleTitleUpdate}
              />
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.title.value = undefined;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid
        container
        spacing={0}
        direction="row"
        alignItems="stretch"
        justifyContent="center"
        sx={{
          marginTop: 4,
          paddingX: '12px',
        }}
      >
        {menuItems}
      </Grid>
      {isEditing && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
          <Button color="primary" onClick={handleAddSubBlock}>
            <AddCircleRoundedIcon />
          </Button>
        </Stack>
      )}
    </Grid>
  );
}

export default BlockMenuList;
